/* ============================================================
   ILS — Popular searches (real data) + header search logging.
   TASK-2064.

   Two responsibilities, one file:

   1. LOG every search that leaves the storefront:
      - submit of any `<form class="ils-search">` (top-bar and
        popular-search-panel copies of the form).
      - click on any `.ils-suggest__chip` (both the SSR chips
        Liquid renders and the ones we swap in below).
      Fire POST {apiBase}/api/search/log with keepalive:true so
      the navigation the browser is already committing to isn't
      held up by the fetch. Body: { q, locale, session_hash }.
      session_hash is a random per-browser id in localStorage —
      no PII, no cookies.

   2. LOAD the top queries for the current locale on page ready
      and swap the SSR chips for the live top-N. If the fetch
      fails, returns empty, or there's no apiBase configured,
      the Liquid fallback ("Dried mango, Lokum, ...") stays in
      place — the storefront never sees an empty panel.

   Locale detection:
      - `window.ILS_LANG` (set globally by theme.liquid from
        request.locale.iso_code — the actual locale of the
        current URL, not the store default).
      - Reduced to the first 2-letter code and whitelisted to
        ru | de | en | lv | et | lt. Anything else -> no locale
        filter (query the global top).

   apiBase is read from `window.ILS_REVIEWS_HOME.apiBase`, the
   same seed used by custom-orders-count-live.js and the reviews
   patchers. The header section emits this global too so the
   variable is populated on every page (not just the home page
   where custom-reviews-home used to be the only emitter).

   Defensive: soft-fail on every branch. Never disturbs the
   Liquid fallback if anything is off (no apiBase / fetch fails
   / items[] empty / etc.).
   ============================================================ */
(function () {
  'use strict';

  var SID_KEY = 'ils_search_sid';
  var VALID_LOCALES = /^(ru|de|en|lv|et|lt)$/;
  var CHIP_LIMIT = 7;
  var CHIP_DAYS = 7;

  function safe(fn) {
    return function () {
      try { fn.apply(null, arguments); } catch (_) { /* no-op */ }
    };
  }

  function getSessionHash() {
    try {
      var v = window.localStorage && window.localStorage.getItem(SID_KEY);
      if (v && v.length >= 8 && v.length <= 64) return v;
      var s = 'sid-' + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
      if (window.localStorage) window.localStorage.setItem(SID_KEY, s);
      return s;
    } catch (_) { return null; }
  }

  function getLocale() {
    var raw = String(window.ILS_LANG || '').toLowerCase().trim();
    if (!raw) return null;
    var first = raw.split(/[-_]/)[0];
    return VALID_LOCALES.test(first) ? first : null;
  }

  function getApiBase() {
    var cfg = window.ILS_REVIEWS_HOME || {};
    if (typeof cfg.apiBase === 'string' && cfg.apiBase) {
      return cfg.apiBase.replace(/\/+$/, '');
    }
    return null;
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function logQuery(q) {
    var api = getApiBase();
    if (!api) return;
    var s = String(q == null ? '' : q).replace(/\s+/g, ' ').trim();
    if (s.length < 2 || s.length > 200) return;
    var payload = {
      q: s,
      locale: getLocale(),
      session_hash: getSessionHash(),
    };
    try {
      fetch(api + '/api/search/log', {
        method: 'POST',
        keepalive: true,
        credentials: 'omit',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }).catch(function () { /* fire-and-forget */ });
    } catch (_) { /* no-op */ }
  }

  function bindForm(form) {
    if (!form || form.dataset.ilsSearchLogBound === '1') return;
    form.dataset.ilsSearchLogBound = '1';
    form.addEventListener('submit', safe(function () {
      var input = form.querySelector('input[type="search"]');
      if (input) logQuery(input.value);
    }));
  }

  function bindChip(chip) {
    if (!chip || chip.dataset.ilsSearchLogBound === '1') return;
    chip.dataset.ilsSearchLogBound = '1';
    chip.addEventListener('click', safe(function () {
      logQuery(chip.textContent);
    }));
  }

  function bindAll() {
    var forms = document.querySelectorAll('form.ils-search');
    for (var i = 0; i < forms.length; i++) bindForm(forms[i]);
    var chips = document.querySelectorAll('.ils-suggest__chip');
    for (var j = 0; j < chips.length; j++) bindChip(chips[j]);
  }

  function deriveSearchBaseUrl(existingChip) {
    // The SSR chips carry the localized `/search` URL (Shopify prefixes
    // it with `/ru-eu`, `/de-de`, etc. on non-primary markets). Reuse it
    // so replacement chips stay inside the current market/locale.
    if (existingChip) {
      var href = existingChip.getAttribute('href') || '';
      var pathOnly = href.split('?')[0];
      if (pathOnly) return pathOnly;
    }
    return '/search';
  }

  function paintChips(items) {
    if (!Array.isArray(items) || !items.length) return false;
    var containers = document.querySelectorAll('.ils-suggest__chips');
    if (!containers.length) return false;
    var painted = false;
    for (var i = 0; i < containers.length; i++) {
      var container = containers[i];
      var firstChip = container.querySelector('.ils-suggest__chip');
      var baseUrl = deriveSearchBaseUrl(firstChip);
      var html = '';
      var take = Math.min(items.length, CHIP_LIMIT);
      for (var k = 0; k < take; k++) {
        var term = String(items[k] && items[k].q || '').trim();
        if (!term) continue;
        var href = baseUrl + '?q=' + encodeURIComponent(term)
          + '&type=product&options%5Bprefix%5D=last';
        html += '<a class="ils-suggest__chip" href="' + href + '">'
          + escapeHtml(term) + '</a>';
      }
      if (html) {
        container.innerHTML = html;
        painted = true;
      }
    }
    if (painted) bindAll();
    return painted;
  }

  function loadTop() {
    var api = getApiBase();
    if (!api) return;
    var locale = getLocale();
    var url = api + '/api/search/top?days=' + CHIP_DAYS + '&limit=' + CHIP_LIMIT
      + (locale ? '&locale=' + encodeURIComponent(locale) : '');
    fetch(url, { credentials: 'omit' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (!data || !Array.isArray(data.items) || !data.items.length) return;
        paintChips(data.items);
      })
      .catch(function () { /* Liquid fallback stays */ });
  }

  function init() {
    bindAll();
    loadTop();
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);
})();
